// Miniatura de um modelo de story desenhada no navegador a partir do MESMO
// layout que o bot usa (computeStoryLayout), em escala. A imagem final, com a
// fonte do envio, vem do bot na geracao.

import { useMemo } from "react";
import { STORY_HEIGHT, STORY_WIDTH, WATERMARK_SAMPLE_DATA, computeStoryLayout, type StoryDesign } from "@lucralink/shared";

interface StoryThumbProps {
  readonly design: StoryDesign;
  /** Fundo do template proprio (URL assinada). */
  readonly backgroundUrl?: string | null;
  readonly width?: number;
}

function pct(value: number, total: number): string {
  return `${(value / total) * 100}%`;
}

/** Fonte que faz o texto caber na caixa, quebrando em linhas (negrito ~0,65 em por
 *  caractere, entrelinha 1,25, folga de 15% para a quebra por palavra). Sem isso
 *  titulos longos vazam da miniatura. */
export function thumbFontSize(rect: { w: number; h: number }, text: string, scale: number): number {
  const boxW = rect.w * scale;
  const boxH = rect.h * scale;
  const fit = 0.85 * Math.sqrt((boxW * boxH) / (Math.max(text.length, 1) * 0.65 * 1.25));
  // Sem espaco nao ha quebra de linha: a palavra inteira precisa caber na largura.
  const oneLine = text.includes(" ") ? Infinity : (boxW * 0.9) / (Math.max(text.length, 1) * 0.65);
  return Math.max(4, Math.min(boxH * 0.42, fit, oneLine));
}

export function StoryThumb({ design, backgroundUrl = null, width = 120 }: StoryThumbProps): JSX.Element {
  const layout = useMemo(() => computeStoryLayout(design, WATERMARK_SAMPLE_DATA), [design]);
  const bg = layout.background;
  const background =
    bg.kind === "gradient" ? `linear-gradient(180deg, ${bg.from}, ${bg.to})` : bg.kind === "solid" ? bg.color : backgroundUrl ? `center / cover url("${backgroundUrl}")` : "#333";
  const scale = width / STORY_WIDTH;
  return (
    <div className="relative overflow-hidden rounded-md" style={{ width, height: width * (STORY_HEIGHT / STORY_WIDTH), background }} aria-hidden="true">
      {layout.photoCard && (
        <div className="absolute" style={{ left: pct(layout.photoCard.rect.x, STORY_WIDTH), top: pct(layout.photoCard.rect.y, STORY_HEIGHT), width: pct(layout.photoCard.rect.w, STORY_WIDTH), height: pct(layout.photoCard.rect.h, STORY_HEIGHT), background: layout.photoCard.color, borderRadius: layout.photoCard.radius * scale }} />
      )}
      <div className="absolute flex items-center justify-center bg-neutral-200/80 text-[8px] text-neutral-500" style={{ left: pct(layout.photoRect.x, STORY_WIDTH), top: pct(layout.photoRect.y, STORY_HEIGHT), width: pct(layout.photoRect.w, STORY_WIDTH), height: pct(layout.photoRect.h, STORY_HEIGHT) }}>
        foto
      </div>
      {layout.ops.map((op, i) =>
        "text" in op ? (
          <div
            key={i}
            className="absolute flex items-center justify-center overflow-hidden text-center font-bold leading-tight"
            style={{
              left: pct(op.rect.x, STORY_WIDTH),
              top: pct(op.rect.y, STORY_HEIGHT),
              width: pct(op.rect.w, STORY_WIDTH),
              height: pct(op.rect.h, STORY_HEIGHT),
              fontSize: thumbFontSize(op.rect, op.text, scale),
              color: op.kind === "text" ? op.color : op.kind === "badge" ? op.textColor : op.textColor,
              background: op.kind === "badge" ? op.color : op.kind === "text" ? (op.background ?? "transparent") : op.color,
              borderRadius: op.kind === "badge" ? (op.shape === "circle" ? "50%" : op.shape === "pill" ? 999 : 2) : 4,
              transform: op.kind !== "band" && op.rotation ? `rotate(${op.rotation}deg)` : undefined,
            }}
          >
            {op.text}
          </div>
        ) : null,
      )}
    </div>
  );
}
